<template>
  <Layout>
    <McLoading :isLoading="isSubmitting" />
    <AlertQuota :quotaStatus="quotaStatus" />
    <CreateRoErrorBanner v-if="isRoSwatActive" :errors="swatMobilityError" />

    <PageHeader :title="title">
      <template #afterTitle>
        <div class="d-flex align-items-center gap-2">
          <McButton
            variant="secondary"
            icon="icon-outline_close"
            :text="$t('global.action.cancel')"
            :disabled="isSubmitting"
            @click="handleCancel"
          />
          <McButton
            v-if="activeKey === 'set-parameters'"
            variant="secondary"
            :text="selectedContent.secondaryBtn.title"
            :icon="selectedContent.secondaryBtn.icon"
            :disabled="disableSecondaryBtn"
            @click="handleClickSecondaryBtn"
          />
          <McButton
            variant="primary"
            :text="selectedContent.primaryBtn.title"
            :icon="selectedContent.primaryBtn?.icon"
            :disabled="disablePrimaryBtn"
            @click="handleClickPrimaryBtn"
          />
        </div>
      </template>
    </PageHeader>

    <div
      :class="[
        'd-flex',
        'justify-content-between',
        'align-items-center',
      ]"
    >
      <McLinearWizard :activeKey="activeKey" :items="wizardItems" />
      <QuotaCounter />
    </div>

    <FormulateForm ref="form-ref" v-model="formValues">
      <AttachDelivery
        v-show="activeKey === 'attach-delivery'"
        ref="attach-delivery-ref"
        class="mt-3"
        :formValues="formValues"
        :fullObjects="fullObjects"
        :isRoSwatActive="isRoSwatActive"
        :originAddressIdRoSwat="originAddressIdRoSwat"
        @update:formValues="handleUpdateFormValues"
        @update:fullObjects="({ key, value }) => $set(fullObjects, key, value)"
      />
      <SetParameters
        v-show="activeKey === 'set-parameters'"
        ref="set-parameters-ref"
        class="mt-3"
        :formValues="formValues"
        :fullObjects="fullObjects"
        :isLoading="isSubmitting"
        :isVisible="activeKey === 'set-parameters'"
        @update:formValues="handleUpdateFormValues"
      />
    </FormulateForm>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'
import { Helper } from '@/mixin/helper'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McLinearWizard from '@/components/McLinearWizard'
import McWizard from '@/components/McWizard'

import AttachDelivery from './contents/AttachDelivery'
import SetParameters from './contents/SetParameters'
import QuotaCounter from './components/QuotaCounter'
import AlertQuota from './components/QuotaCounter/AlertQuota'
import getWizardItems from './lib/constants/wizard-items.js'
import CreateRoErrorBanner from './components/CreateRoErrorBanner'

import * as RO from '@/services/rest-api/lib/route-optimization'

import { errorMappings } from './lib/constants/error-mappings'
import { errorV2Mappings } from './lib/constants/error-v2-mappings'
import { FORM_CREATE_ROUTE_OPTIMIZE_DEFAULT } from './lib/constants/default-values'
import { errorCodeToastMap } from './lib/constants/error-code-mappings'
import { EventBus } from './lib/event-bus.js'

import { cloneDeep, set } from 'lodash'
import moment from 'moment'
import { mapGetters } from 'vuex'

export default {
  page: {
    title: 'Route Optimization',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButton,
    McLinearWizard,
    McWizard,
    AttachDelivery,
    SetParameters,
    QuotaCounter,
    AlertQuota,
    CreateRoErrorBanner,
  },
  mixins: [Helper],
  data() {
    return {
      isSubmitting: false,
      isOncePressed: false,
      activeKey: 'attach-delivery',
      formValues: cloneDeep(FORM_CREATE_ROUTE_OPTIMIZE_DEFAULT),
      fullObjects: {},
      quotaStatus: 'available',
      swatMobilityError: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
      getFeatureStrategies: 'identity/getFeatureStrategies',
    }),
    hasTmsUserGroupAccess() {
      return (
        this.hasAccessAll(['tms-user-group:read'])
      )
    },
    title() {
      return this.$t('routeOptimization.createTitle')
    },
    wizardItems() {
      return getWizardItems(this)
    },
    contentItems() {
      return {
        'attach-delivery': {
          component: AttachDelivery,
          primaryBtn: {
            title: this.$t('global.next'),
            icon: 'icon-outline_arrow-right',
          },
        },
        'set-parameters': {
          component: SetParameters,
          secondaryBtn: {
            title: this.$t('global.prev'),
            icon: 'icon-outline_arrow-left',
          },
          primaryBtn: {
            title: this.$t('routeOptimization.action.runOptimization'),
          },
        },
      }
    },
    selectedContent() {
      return this.contentItems[this.activeKey]
    },
    disableSecondaryBtn() {
      return false || this.isSubmitting
    },
    disablePrimaryBtn() {
      if (this.isSubmitting || this.isOncePressed) return true

      if (this.activeKey === 'set-parameters') {
        const hasDeparture = !!this.formValues.expected_departure_on
        const hasValidServiceTime =
          moment.duration(this.formValues['options.service_time']).asMilliseconds() >=
          moment.duration('PT3M').asMilliseconds()
        const hasVehicles = this.formValues.vehicle_ids.length > 0
        return !(hasDeparture && hasValidServiceTime && hasVehicles)
      }

      const hasStartAddress = this.isRoSwatActive ? true : !!this.formValues.start_address_id
      const hasDeliveryOrders = this.formValues.delivery_order_ids.length > 0

      return !(hasStartAddress && hasDeliveryOrders)
    },
    isRoSwatActive() {
      const isSamator = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.is_samator || false
      const isActive = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.active || false
      
      return isSamator && isActive
    },
    originAddressIdRoSwat() {
      const originAddressId = this.getFeatureStrategies('ro.swat-mobility', this.getCompanyId())?.origin_address_id || null
      return originAddressId
    },
  },
  created() {
    EventBus.$on('update-form-values', this.handleUpdateFormValues)
  },
  mounted() {
    this.loadLocalConfig()
    this.loadUnservedData()
  },
  beforeDestroy() {
    EventBus.$off('update-form-values', this.handleUpdateFormValues)
  },
  methods: {
    getUnservedData() {
      return JSON.parse(sessionStorage.getItem('createRouteOptimizeUnserved') || '[]')
    },

    updateUnservedData(data) {
      if (data.length > 0) {
        sessionStorage.setItem('createRouteOptimizeUnserved', JSON.stringify(data))
      } else {
        sessionStorage.removeItem('createRouteOptimizeUnserved')
      }
    },

    findRoDataById(roId) {
      const unservedData = this.getUnservedData()
      const targetData = unservedData.find(item => item[roId])
      return targetData?.[roId] || null
    },

    removeRoDataById(roId) {
      const unservedData = this.getUnservedData()
      const filteredData = unservedData.filter(item => !item[roId])
      this.updateUnservedData(filteredData)
    },

    handleCancel() {
      this.$router.push({ name: 'Route Optimization' })
    },

    handleClickSecondaryBtn() {
      if (this.activeKey === 'attach-delivery') return

      this.activeKey = 'attach-delivery'
    },

    handleClickPrimaryBtn() {
      if (this.activeKey === 'attach-delivery') {
        this.activeKey = 'set-parameters'

        return
      }

      this.handleSubmit()
    },

    handleUpdateFormValues({ key, value }) {
      this.$set(this.formValues, key, value)
    },

    async handleSubmit() {
      if (this.$refs['form-ref'].hasErrors) {
        this.$mcToast.error(this.$t('routeOptimization.alert.inputFormatError'))
        return
      }
      this.isOncePressed = true
      this.isSubmitting = true

      // Clean up formValues from unwanted auto-generated keys by Vue Formulate
      Object.keys(this.formValues).forEach((key) => {
        if (key.includes('select-resourceful-v2_') || key === 'ignore_traffic_level') {
          this.$delete(this.formValues, key)
        }
      })

      const data = {
        type: 'SINGLE_PICK-MULTI_DROP',
      }

      for (const [key, value] of Object.entries(this.formValues)) {
        if (value === null || key === 'delivery_order_ids' || key.includes('select-resourceful-v2_') || key === 'ignore_traffic_level') continue
        
        if (key === 'user_group_id' && !(this.hasTmsUserGroupAccess && this.isSuperUser())) continue

        if (key === 'user_group_id' && value) {
          set(data, key, Number(value))
        } else {
          set(data, key, value)
        }
      }

      const {
        start_address_id,
        delivery_order_ids,
        delivery_orders,
        vehicle_ids,
        user_group_id,
        ...configToSave
      } = this.formValues

      const userConfiguration = {
        ...configToSave,
        company_id: this.getCompanyId(),
        user_id: this.getUserId(),
      }

      const existingConfig = JSON.parse(localStorage.getItem('roConfiguration') || '{}')
      const currentUserId = this.getUserId()
      existingConfig[currentUserId] = userConfiguration
      localStorage.setItem('roConfiguration', JSON.stringify(existingConfig))

      let res = null
      try {
        res = await RO.store(data)
      } catch (error) {
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
        this.isSubmitting = false
        this.isOncePressed = false
        return
      }

      if (res.errors) {
        if (res.status === 504) {
          this.isSubmitting = false
          this.isOncePressed = false
          return false
        }
        const errors = res
        let errorMessage = this.$t('routeOptimization.alert.inputFormatError')
        if (errorMappings[errors.message]) {
          errorMessage = this.$t(errorMappings[errors.message])
        }

        if (this.isRoSwatActive) {
          this.swatMobilityError = res.errors
        }

        if (res.errors_v2 !== null) {
          // Check for error codes in errorCodeToastMap first
          const issue = res.errors_v2.issues.find((issue) => errorCodeToastMap[issue.code])
          if (issue) {
            errorMessage = this.$t(errorCodeToastMap[issue.code])
            if (issue.code === 'FLEET_TASK.ROUTE_OPTIMIZATION_LIMIT_REACHED') {
              this.quotaStatus = 'empty'
              this.$mcToast.error(errorMessage)
              this.isSubmitting = false
              this.isOncePressed = false
              return
            }
          } else if (
            res.errors_v2?.issues[0]?.code === 'FLEET_TASK.DELIVERY_ORDER_INVALID_STATUS'
          ) {
            // reset all values form
            this.activeKey = 'attach-delivery'
            this.formValues = cloneDeep(FORM_CREATE_ROUTE_OPTIMIZE_DEFAULT)
            this.fullObjects = {}
            this.isOncePressed = false

            // reset on set parameters
            this.$refs['set-parameters-ref'].$refs['table-select-vehicle-ref'].ResetTableState()
            this.$refs['set-parameters-ref'].$refs['activity-duration-ref'].ResetFormValues()

            errorMessage = this.$t(errorV2Mappings[res.errors_v2?.issues[0]?.code])
          }
        }

        this.$mcToast.error(errorMessage)
        this.isSubmitting = false
        this.isOncePressed = false
        this.quotaStatus = 'available'
        return
      }

      if (!res?.data?.id) {
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
        this.isSubmitting = false
        this.isOncePressed = false
        return
      }

      const roId = this.$route.query.ro_id
      if (roId) {
        this.removeRoDataById(roId)
      }

      this.isSubmitting = false
      this.$router.push({ name: 'Route Optimization Detail', params: { id: res.data.id } })
    },

    loadLocalConfig() {
      const savedConfig = localStorage.getItem('roConfiguration')
      if (!savedConfig) return

      const parsedConfig = JSON.parse(savedConfig)
      const currentUserId = this.getUserId()
      const currentCompanyId = this.getCompanyId()

      const userConfig = parsedConfig[currentUserId]
      if (!userConfig) return

      if (userConfig.company_id !== currentCompanyId) {
        return
      }

      const { company_id, user_id, ...configToApply } = userConfig

      const filteredConfig = Object.keys(configToApply)
        .filter((key) => !key.includes('select-resourceful-v2_') && key !== 'ignore_traffic_level')
        .reduce((obj, key) => {
          obj[key] = configToApply[key]
          return obj
        }, {})

      this.formValues = {
        ...this.formValues,
        ...filteredConfig,
      }
    },

    loadUnservedData() {
      const roId = this.$route.query.ro_id
      if (!roId) return

      const roData = this.findRoDataById(roId)
      if (!roData) return
      
      if (roData.origin_address) {
        this.$set(this.formValues, 'start_address_id', roData.origin_address.id)
        this.$set(this.fullObjects, 'start_address_id', roData.origin_address)
      }
      
      if (roData.selected_unserved && roData.selected_unserved.length > 0) {
        this.$nextTick(() => {
          this.$set(this.formValues, 'delivery_order_ids', [...roData.selected_unserved])
          
          const delivery_orders = roData.selected_unserved.map(id => ({
            id: id,
            priority: 'regular'
          }))
          this.$set(this.formValues, 'delivery_orders', delivery_orders)
        })
      }

      if (this.hasTmsUserGroupAccess && this.isSuperUser() && roData?.user_group) {
        this.$set(this.formValues, 'user_group_id', roData.user_group?.id)
        this.$set(this.fullObjects, 'user_group', roData.user_group)
      }
    },
  },
}
</script>
